import React, { useRef, useState } from 'react';
import { Download, ArrowLeft, Loader } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { uploadAccountantShipmentPDFs } from '../../utils/s3Upload';
import { API_BASE_URL, apiCall, API_ENDPOINTS } from '../../config/api';
import airlines from '../../config/airlines.json';
import { getServices } from '../../config/servicesSource';

// value → service, from the admin-maintained list (falls back to the bundled
// one). Read per call rather than at import time, so an edit to HSN Codes is
// picked up without a reload.
const lookupService = (value) => getServices().find(s => s.value === value) || {};

// AWB prefix (first 3 digits) → airline IATA carrier code
const getAirlineFromAWB = (awbNumber) => {
  if (!awbNumber) return null;
  const prefix = String(awbNumber).replace(/\D/g, '').substring(0, 3);
  const found = airlines.find(a => a.prefix === prefix);
  return found ? found : null;
};

// Weights print to two decimals — the scraped values carry three ("834.000 KG").
const weightText = (value) => {
  if (value === null || value === undefined || value === '' || value === '-') return '—';
  const num = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? `${num.toFixed(2)} KG` : String(value);
};

const InvoicePreview = ({ formData, shipment, bankDetails, bankName, shipperName, existingInvoiceId, allInvoices = [], customerInvoice = false, onClose, onBack, onSubmitSuccess }) => {
  const invoiceRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  // A customer invoice is raised straight against a customer — no shipment, so
  // none of the shipment blocks are printed and no AWB is sent.
  const isCustomerInvoice = customerInvoice;
  const isSeaShipment = !isCustomerInvoice && String(shipment?.type || shipment?.shipmentType || '').toLowerCase().includes('sea');
  const shipmentIdentifier = String(isSeaShipment ? (shipment?.blNumber || shipment?.bl_number || '') : (shipment?.awbNumber || shipment?.awb_number || '')).trim();

  // ── Convert img src to base64 so html2canvas can render them in PDF ──
  const toBase64 = (url) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null); // fallback: keep original
    img.src = url.startsWith('http') ? url : window.location.origin + url;
  });

  const inlineImages = async (element) => {
    const imgs = element.querySelectorAll('img');
    const restoreFns = [];
    for (const img of imgs) {
      const original = img.src;
      const b64 = await toBase64(img.getAttribute('src') || img.src);
      if (b64) {
        img.src = b64;
        restoreFns.push(() => { img.src = original; });
      }
    }
    return () => restoreFns.forEach(fn => fn());
  };

  // ── Generate PDF blob using html2pdf.js ──
  const generatePdfBlob = async () => {
    const element = invoiceRef.current;
    const originalBoxShadow = element.style.boxShadow;
    const originalBorderRadius = element.style.borderRadius;
    element.style.boxShadow = 'none';
    element.style.borderRadius = '0';

    // Inline all images as base64 before capturing
    const restoreImages = await inlineImages(element);

    const elementWidth = element.offsetWidth;
    const elementHeight = element.scrollHeight;

    const opt = {
      margin: 0,
      filename: `Invoice_${formData.invoiceNumber.replace(/\//g, '-')}_${formData.customerName || shipment.customerName}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        letterRendering: true,
        windowWidth: elementWidth,
        windowHeight: elementHeight,
        width: elementWidth,
        height: elementHeight,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: '#ffffff',
      },
      jsPDF: {
        unit: 'px',
        format: [elementWidth, elementHeight],
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      },
      pagebreak: { mode: ['css', 'legacy'], avoid: ['.no-break', 'tr'] }
    };

    const blob = await html2pdf().set(opt).from(element).output('blob');

    // Restore original image srcs
    restoreImages();
    element.style.boxShadow = originalBoxShadow;
    element.style.borderRadius = originalBorderRadius;

    return blob;
  };

  // Calculate line item totals (rounding on grand total only)
  const calculateLineItemTotal = (item) => {
    const rate = parseFloat(String(item.rate).replace(/,/g, '')) || 0;
    const qty = parseFloat(String(item.qty).replace(/,/g, '')) || 0;
    return rate * qty;
  };

  const calculateLineItemGST = (item) => {
    const total = calculateLineItemTotal(item);
    const gst = parseFloat(item.gst) || 0;
    return (total * gst) / 100;
  };

  // Calculate invoice totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalGST = 0;

    formData.lineItems.forEach(item => {
      subtotal += calculateLineItemTotal(item);
      totalGST += calculateLineItemGST(item);
    });

    // Check if intra-state (Delhi) or inter-state based on GSTIN
    const isIntraState = formData.customerGSTIN.startsWith('07');

    // Round tax values first, then derive grand total from them so displayed numbers always add up
    const cgst = isIntraState ? Math.ceil(totalGST / 2) : 0;
    const sgst = isIntraState ? Math.ceil(totalGST / 2) : 0;
    const igst = !isIntraState ? Math.ceil(totalGST) : 0;
    const grandTotal = Math.ceil(isIntraState ? subtotal + cgst + sgst : subtotal + igst);

    return {
      subtotal,
      totalGST,
      grandTotal,
      isIntraState,
      cgst,
      sgst,
      igst
    };
  };

  const totals = calculateTotals();

  // Convert number to words (Indian format)
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    if (num === 0) return 'Zero';

    let word = '';
    
    // Crores
    if (num >= 10000000) {
      word += numberToWords(Math.floor(num / 10000000)) + ' Crore ';
      num %= 10000000;
    }
    
    // Lakhs
    if (num >= 100000) {
      word += numberToWords(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }
    
    // Thousands
    if (num >= 1000) {
      word += numberToWords(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }
    
    // Hundreds
    if (num >= 100) {
      word += ones[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }
    
    // Tens and Ones
    if (num >= 20) {
      word += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    } else if (num >= 10) {
      word += teens[num - 10] + ' ';
      num = 0;
    }
    
    if (num > 0) {
      word += ones[num] + ' ';
    }
    
    return word.trim();
  };

  const amountInWords = (amount) => {
    const rupees = Math.floor(amount);
    const paise = Math.round((amount - rupees) * 100);
    
    let words = 'Rupees ' + numberToWords(rupees);
    if (paise > 0) {
      words += ' and ' + numberToWords(paise) + ' Paise';
    }
    return words + ' Only';
  };

  // ── Build structured invoice payload for POST /invoices ──
  const buildInvoicePayload = async (invoicePdfKey = null) => {
    const rawUserId = shipment.userId || shipment.userID || shipment.user_id || null;
    const parsedUserId = rawUserId ? parseInt(rawUserId, 10) : NaN;
    const userId = Number.isInteger(parsedUserId) && parsedUserId > 0 ? parsedUserId : null;
    const isIntraState = formData.customerGSTIN.startsWith('07');

    const identifier = shipmentIdentifier;

    // invoice_data carries this invoice only. It used to be prepended to every
    // earlier entry for the same AWB, so a record ended up listing numbers that
    // belonged to previous invoices.
    const invoiceData = [{
      invoiceNumber: formData.invoiceNumber,
      invoiceDate: formData.invoiceDate,
    }];

    const items = formData.lineItems.map((item, idx) => {
      const svc = lookupService(item.description);
      const descLabel = svc.label || item.description;
      const hsnCode = item.hsn || svc.hsn || '';
      const gstRate = parseFloat(item.gst || svc.gst || '18') || 18;
      const rate = parseFloat(String(item.rate).replace(/,/g, '')) || 0;
      // Qty is a whole number on an invoice — round here too, in case a value
      // reached the form from somewhere that didn't go through its input.
      const qty  = Math.round(parseFloat(String(item.qty).replace(/,/g, '')) || 0);
      const taxableAmount = Math.ceil(rate * qty);
      const gstAmt = Math.ceil(taxableAmount * gstRate / 100);
      const cgst  = isIntraState ? Math.ceil(gstAmt / 2) : 0;
      const sgst  = isIntraState ? Math.ceil(gstAmt / 2) : 0;
      const igst  = !isIntraState ? gstAmt : 0;
      const itemTotal = taxableAmount + (isIntraState ? cgst + sgst : igst);
      return {
        item_id: idx + 1,
        description: descLabel,
        hsn_sac: hsnCode || null,
        quantity: qty,
        unit: 'service',
        unit_price: rate,
        discount_percent: 0,
        discount_amount: 0,
        taxable_amount: taxableAmount,
        gst_rate_percent: gstRate,
        cgst,
        sgst,
        igst,
        total: itemTotal
      };
    });

    const subtotal   = items.reduce((s, i) => s + i.taxable_amount, 0);
    const totalCgst  = items.reduce((s, i) => s + i.cgst, 0);
    const totalSgst  = items.reduce((s, i) => s + i.sgst, 0);
    const totalIgst  = items.reduce((s, i) => s + i.igst, 0);
    const totalTax   = totalCgst + totalSgst + totalIgst;
    const grandTotal = subtotal + totalTax;
    const roundOff   = 0;

    const invoiceDateObj = new Date(formData.invoiceDate);
    const dueDateObj = new Date(invoiceDateObj);
    dueDateObj.setDate(dueDateObj.getDate() + 14);
    const dueDate = dueDateObj.toISOString().split('T')[0];

    return {
      // 'shipment' is the default on the API; a customer invoice carries no AWB
      // and is billed to a row of the customer master instead.
      ...(isCustomerInvoice
        ? { invoice_type: 'customer', customer_id: formData.customerId ?? null }
        : { invoice_type: 'shipment', user_id: userId }),
      ...(isCustomerInvoice ? {} : (isSeaShipment ? { bl_number: identifier } : { awb_number: identifier })),
      ...(invoicePdfKey ? { invoice_pdf_key: invoicePdfKey } : {}),
      ...(isSeaShipment ? { 
        sb_no: shipment.sbNo || shipment.sb_no || shipment.sbNumber || null,
        sb_date: shipment.sbDate || shipment.sb_date || null 
      } : {}),
      invoice: {
        invoice_id: isCustomerInvoice
          ? `INV-CUST${formData.customerId ?? ''}-${formData.invoiceNumber.replace(/\//g, '-')}`
          : `INV-${identifier}-${formData.invoiceNumber.replace(/\//g, '-')}`,
        invoice_data: invoiceData,
        due_date: dueDate,
        status: 'unpaid',
        currency: 'INR',
        seller: {
          name: 'HMR Spedition Pvt. Ltd.',
          address: 'AG-11, R.G. City Centre, Motia Khan, Paharganj, New Delhi-110055 (INDIA)',
          gstin: '07AAECH9732M2Z2',
          email: 'accounts@hmrspedition.com',
          phone: '+91-11-4567-8900'
        },
        buyer: {
          // Who the invoice is billed to: the customer master row for a
          // customer invoice, the shipment's user for a shipment one.
          customer_id: isCustomerInvoice
            ? (formData.customerId != null ? String(formData.customerId) : null)
            : (userId != null ? String(userId) : null),
          name: formData.customerName || null,
          address: formData.customerAddress || null,
          gstin: formData.customerGSTIN || null,
          email: null,
          phone: null
        },
        items,
        summary: {
          subtotal,
          total_discount: 0,
          taxable_amount: subtotal,
          total_cgst: totalCgst,
          total_sgst: totalSgst,
          total_igst: totalIgst,
          total_tax: totalTax,
          round_off: roundOff,
          grand_total: grandTotal
        },
        payment: {
          method: null,
          transaction_id: null,
          paid_amount: 0,
          paid_date: null,
          bank_name: bankDetails.bankName,
          account_number: bankDetails.accountNumber,
          ifsc: bankDetails.ifscCode,
          upi_id: bankName === 'HDFC' ? 'hmrspedition@hdfcbank' : 'hmrspedition@icici'
        },
        notes: 'Payment due within 14 days.',
        terms: 'Late payment will incur 2% monthly interest.'
      }
    };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const warnings = [];
    try {
      // ── 1. Generate the PDF, but keep it in memory ──
      // Nothing is downloaded or uploaded until the invoice record itself has
      // been accepted: a rejected invoice (duplicate number, bad payload) used
      // to leave a PDF in the user's Downloads and a file on S3 for an invoice
      // that does not exist.
      const pdfBlob = await generatePdfBlob();

      // ── 2. Save the invoice record first — this is the step that can fail ──
      const isEditMode = Boolean(existingInvoiceId);
      const invoiceEndpoint = isEditMode
        ? `${(isSeaShipment ? API_ENDPOINTS.INVOICE_UPDATE_SEA : API_ENDPOINTS.INVOICE_UPDATE)}/${existingInvoiceId}`
        : (isSeaShipment ? API_ENDPOINTS.INVOICE_SUBMIT_SEA : API_ENDPOINTS.INVOICE_SUBMIT);

      const invoiceRes = await apiCall(invoiceEndpoint, {
        method: isEditMode ? 'PATCH' : 'POST',
        body: JSON.stringify(await buildInvoicePayload(null)),
      });
      const invoiceBody = await invoiceRes.json().catch(() => ({}));
      if (!invoiceRes.ok || invoiceBody?.success === false) {
        throw new Error(invoiceBody?.message || `Invoice could not be saved (HTTP ${invoiceRes.status})`);
      }
      const savedInvoiceId =
        existingInvoiceId || invoiceBody?.data?.id || invoiceBody?.id || null;

      // ── 3. Upload to S3 ──
      let invoicePdfKey = null;
      const pdfFile = new File([pdfBlob], `INVOICE_${shipmentIdentifier}.pdf`, { type: 'application/pdf' });

      if (isSeaShipment) {
        try {
          const presignedResponse = await apiCall(API_ENDPOINTS.SEA_EXPORT_ACCOUNTANT_PRESIGNED_URLS, {
            method: 'POST',
            body: JSON.stringify({
              blNumber: shipmentIdentifier,
              files: [{ type: 'INVOICE', fileName: pdfFile.name }]
            })
          });
          const presignedData = await presignedResponse.json();
          if (!presignedResponse.ok || !presignedData.success || !presignedData.data?.invoicePdf?.uploadUrl) {
            throw new Error(presignedData?.message || 'Failed to get sea invoice upload URL');
          }

          invoicePdfKey = presignedData.data.invoicePdf.key;
          const uploadResponse = await fetch(presignedData.data.invoicePdf.uploadUrl, {
            method: 'PUT',
            body: pdfFile,
            headers: {
              'Content-Type': 'application/pdf'
            }
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload sea invoice PDF');
          }
        } catch (seaUploadErr) {
          console.warn('Sea S3 Upload warning:', seaUploadErr.message);
          warnings.push('S3 upload: ' + seaUploadErr.message);
        }
      } else if (isCustomerInvoice) {
        // No AWB to hang the file on — the customer-invoice category keys it by
        // customer and invoice number instead:
        // docs/CUSTOMER-INVOICE/<customerId>/<invoice-number>.pdf
        try {
          const presignRes = await apiCall(API_ENDPOINTS.S3_UPLOAD_URL, {
            method: 'POST',
            body: JSON.stringify({
              files: [{
                uploadCategory: 'customer-invoice',
                customerId: formData.customerId,
                invoiceNumber: formData.invoiceNumber,
                contentType: 'application/pdf',
              }],
            }),
          });
          const presign = await presignRes.json().catch(() => ({}));
          const entry = Array.isArray(presign?.data) ? presign.data[0] : null;
          if (!presignRes.ok || presign?.success === false || !entry?.uploadUrl) {
            throw new Error(entry?.error || presign?.message || `Could not get an upload URL (HTTP ${presignRes.status})`);
          }
          const put = await fetch(entry.uploadUrl, {
            method: 'PUT',
            body: pdfFile,
            headers: { 'Content-Type': 'application/pdf' },
          });
          if (!put.ok) throw new Error(`S3 responded ${put.status}`);
          invoicePdfKey = entry.key;
        } catch (custUploadErr) {
          console.warn('Customer invoice S3 upload warning:', custUploadErr.message);
          warnings.push('S3 upload: ' + custUploadErr.message);
        }
      } else {
        const uploadResult = await uploadAccountantShipmentPDFs({ invoice: pdfFile }, shipmentIdentifier);
        if (uploadResult.success) {
          invoicePdfKey = uploadResult.keys?.invoicePdf || null;
        } else {
          console.warn('S3 Upload warning:', uploadResult.error);
          warnings.push('S3 upload: ' + (uploadResult.error || 'Unknown error'));
        }
      }

      // ── 4. Attach the S3 key to the record saved in step 2 ──
      if (invoicePdfKey && savedInvoiceId) {
        try {
          const patchEndpoint = `${(isSeaShipment ? API_ENDPOINTS.INVOICE_UPDATE_SEA : API_ENDPOINTS.INVOICE_UPDATE)}/${savedInvoiceId}`;
          const patchRes = await apiCall(patchEndpoint, {
            method: 'PATCH',
            body: JSON.stringify(await buildInvoicePayload(invoicePdfKey)),
          });
          if (!patchRes.ok) {
            const errData = await patchRes.json().catch(() => ({}));
            warnings.push('Invoice PDF link: ' + (errData.message || `HTTP ${patchRes.status}`));
          }
        } catch (patchErr) {
          warnings.push('Invoice PDF link: ' + patchErr.message);
        }
      }

      // ── 5. Store the key on the shipment record ──
      // The key is stored on the shipment record, so this step only applies to
      // a shipment invoice.
      if (invoicePdfKey && !isCustomerInvoice) {
        if (isSeaShipment) {
          try {
            const response = await apiCall(API_ENDPOINTS.SEA_EXPORT_ACCOUNTANT_UPDATE, {
              method: 'POST',
              body: JSON.stringify({ blNumber: shipmentIdentifier, invoicePdfKey })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
              console.warn('Sea backend save warning:', data.message);
              warnings.push('Backend: ' + (data.message || `HTTP ${response.status}`));
            }
          } catch (backendErr) {
            console.warn('Sea backend save error:', backendErr.message);
            warnings.push('Backend: ' + backendErr.message);
          }
        } else {
          const shipmentType = shipment.service || shipment.shipmentType || 'gmr';
          const token = localStorage.getItem('token');
          try {
            const response = await fetch(`${API_BASE_URL}/awb/${shipmentType}/accountant`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ awbNumber: shipmentIdentifier, invoicePdfKey }),
            });
            const data = await response.json();
            if (!response.ok) {
              console.warn('Backend save warning:', data.message);
              warnings.push('Backend: ' + (data.message || `HTTP ${response.status}`));
            }
          } catch (backendErr) {
            console.warn('Backend save error:', backendErr.message);
            warnings.push('Backend: ' + backendErr.message);
          }
        }
      }

      // ── 6. The invoice exists — now hand the PDF to the user ──
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${formData.invoiceNumber?.replace(/\//g, '-')}_${formData.customerName || shipment.customerName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // ── 7. Refresh the table and close ──
      window.dispatchEvent(new Event('shipmentCreated'));
      if (warnings.length > 0) {
        console.warn('Submit completed with warnings:', warnings);
        alert('Invoice saved and downloaded, but some steps failed and may need a retry:\n\n' + warnings.join('\n'));
      }
      if (onSubmitSuccess) onSubmitSuccess();
      else if (onClose) onClose();

    } catch (err) {
      console.error('Submit error:', err);
      alert(`Invoice not saved — nothing was downloaded or uploaded.\n\n${err.message}`);
      setSubmitting(false);
    }
  };

  const totalsForBar = formData.lineItems.reduce((sum, item) => {
    const rate = parseFloat(String(item.rate).replace(/,/g, '')) || 0;
    const qty = Math.round(parseFloat(String(item.qty).replace(/,/g, '')) || 0);
    const taxable = Math.ceil(rate * qty);
    return sum + taxable + Math.ceil((taxable * (parseFloat(item.gst || 18) || 18)) / 100);
  }, 0);

  return (
    <div className="min-h-screen bg-[#f4f7fc] dark:bg-[#080d19]">
      {/* The sheet below is the invoice itself and stays as it prints; this bar
          is the only part of the screen that belongs to the console. */}
      <div className="sticky top-0 z-20 no-print border-b border-slate-200 bg-white/90 backdrop-blur
                      dark:border-slate-800 dark:bg-[#0b1220]/90">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3.5">
          <button
            onClick={onBack}
            disabled={submitting}
            className="inline-flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-bold border transition disabled:opacity-50
                       border-slate-200 bg-white text-slate-700 hover:bg-slate-50
                       dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60"
          >
            <ArrowLeft size={16} /> Back to edit
          </button>

          <div className="min-w-0 hidden sm:block">
            <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
              Preview · not saved yet
            </p>
            <p className="text-[13px] font-bold text-slate-900 dark:text-white truncate">
              {formData.invoiceNumber}
              <span className="font-medium text-slate-400"> · {formData.customerName || shipment.customerName || '—'}</span>
            </p>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Grand total</p>
              <p className="text-[15px] font-black text-slate-900 dark:text-white leading-tight">
                ₹{totalsForBar.toLocaleString('en-IN')}
              </p>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold text-white
                         bg-emerald-600 hover:bg-emerald-700 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating PDF…</>
                : <><Download size={16} /> Save &amp; download</>
              }
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-8 px-4">

        {/* ═══════════════ INVOICE CARD ═══════════════ */}
        <div
          ref={invoiceRef}
          className="bg-white shadow-2xl print:shadow-none"
          /* 900px wide, so one A4 page at the same ratio is 900 x 297/210.
             A short invoice fills that page instead of ending halfway down it;
             a long one simply grows past it, as before. */
          style={{ width: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', minHeight: '1273px' }}
        >

          {/* ── HEADER with Gradient ── */}
          <div className="px-8 py-4" style={{ background: 'linear-gradient(135deg, #0f2167 0%, #1a3fad 50%, #2563eb 100%)', borderBottom: '2px solid #1a3fad' }}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <img src="/images/logos/hmr-logo.png" alt="HMR Logo" className="h-24 w-auto object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                <div>
                  <h1 className="text-2xl font-bold mb-2" style={{ letterSpacing: '0.05em', wordSpacing: '0.15em', color: '#ffffff' }}>HMR SPEDITION PVT. LTD.</h1>
                  <p className="text-xs leading-relaxed" style={{ color: '#bfdbfe' }}>
                    AG-11, R.G. City Centre, Motia Khan,<br />
                    Paharganj, New Delhi-110055 (INDIA)
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="space-y-1 text-xs" style={{ color: '#bfdbfe' }}>
                  <p><span className="font-semibold">CIN:</span> U63030DL2019PTC352375</p>
                  <p><span className="font-semibold">GSTIN:</span> 07AAECH9732M2Z2</p>
                  <p><span className="font-semibold">TAN:</span> DELH13774F</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── PARTY / INVOICE DETAILS SECTION ── */}
          {/* Without the export strip below it, this block needs its own bottom
              padding or it sits flush against the rule. */}
          <div className={`no-break invoice-section px-8 pt-4 ${isCustomerInvoice ? 'pb-5' : 'pb-0'}`} style={{ borderBottom: '3px solid #1a3fad' }}>
            <div className={`grid gap-6 ${isCustomerInvoice ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {/* Customer */}
              <div className="border-r-2 border-gray-300 pr-6">
                <h3 className="text-xs font-bold mb-3 uppercase tracking-wide" style={{ color: '#1a3fad', letterSpacing: '0.1em' }}>Customer:</h3>
                <div className="space-y-1">
                  <p className="font-bold text-gray-900 text-sm">{formData.customerName || shipment.customerName || 'GLOBAL INDUSTRIES'}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {formData.customerAddress || '10/54, GROUND FLOOR, PVT NO 7, W.E., D. B. GUPTA ROAD, KAROL BAGH, NEW DELHI-110005'}
                  </p>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">GSTIN:</span> {formData.customerGSTIN}</p>
                    <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">State Code:</span> {formData.customerGSTIN.substring(0, 2)}</p>
                  </div>
                </div>
              </div>
              {/* Shipper / Consignee — both are shipment parties, so a customer
                  invoice leaves the column out entirely. */}
              {!isCustomerInvoice && (
              <div className="border-r-2 border-gray-300 pr-6">
                <h3 className="text-xs font-bold mb-3 uppercase tracking-wide" style={{ color: '#1a3fad', letterSpacing: '0.1em' }}>Shipper:</h3>
                <div className="space-y-2">
                  <p className="font-bold text-gray-900 text-sm mb-6">{shipperName || formData.customerName || shipment.customerName || 'GLOBAL INDUSTRIES'}</p>
                  <div className="mt-3 space-y-1">
                    <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#1a3fad', letterSpacing: '0.1em' }}>Consignee:</h3>
                    <p className="font-bold text-gray-900 text-sm">{shipment.buyerName || 'DCW SOLUTIONS INC'}</p>
                  </div>
                </div>
              </div>
              )}
              {/* Invoice Details */}
              <div>
                <h3 className="text-xs font-bold mb-3 uppercase tracking-wide" style={{ color: '#1a3fad', letterSpacing: '0.1em' }}>Invoice Details</h3>
                {/* Half the row is a wide column for four short rows — cap it so
                    the label and its value don't drift apart. */}
                <div className={`space-y-1.5 ${isCustomerInvoice ? 'max-w-[280px]' : ''}`}>
                  {[
                    ['Invoice No.:', formData.invoiceNumber],
                    ['Invoice Date:', new Date(formData.invoiceDate).toLocaleDateString('en-IN')],
                    ...(isCustomerInvoice ? [] : [
                      ['Mode:', isSeaShipment ? 'Sea' : 'Air'],
                      [isSeaShipment ? 'B/L No.:' : 'AWB No.:', shipmentIdentifier || '—'],
                      ['SB Date:', shipment.sbDate || new Date().toLocaleDateString('en-IN')],
                      ['SB No(s):', shipment.sbNo || shipment.sbNumber || '—'],
                    ]),
                    ['Supply Type:', formData.supplyType],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span className="font-semibold text-gray-700 w-24">{k}</span>
                      <span className="text-gray-900 font-bold text-right flex-1">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Export Details Row — shipment data, so a customer invoice omits it */}
            {!isCustomerInvoice && (
            <div className="mt-4 -mx-8" style={{ background: 'linear-gradient(90deg, rgb(189 217 236 / 30%) 0%, rgb(200 216 240 / 23%) 100%)', padding: '10px 40px 14px 40px' }}>
              {/* Row 1 — 5 items */}
              <div className="grid grid-cols-5 gap-8 mb-5">
                {(
                  isSeaShipment
                    ? [
                        ['POL:', shipment.portOfLoading || shipment.pol || '—'],
                        ['POD:', shipment.portOfDischargeCode || shipment.pod || '—'],
                        ['Gross Wt.:', weightText(shipment.gwt)],
                        ['PKG:', `${shipment.pkg || shipment.totalPkt || '—'} NOS`],
                        ['Destination:', shipment.countryOfFinalDestination || shipment.destCountry || '—'],
                      ]
                    : [
                        ['POL:', shipment.origin || shipment.pol || 'DEL'],
                        ['POD:', shipment.destCode || shipment.pod || '—'],
                        ['Gross Wt.:', weightText(shipment.weight || shipment.grossWeight)],
                        ['PKG:', `${shipment.totalPkt || shipment.numberOfPackages || '—'} NOS`],
                        ['Destination:', shipment.destCity ? `${shipment.destCity}${shipment.destCountry ? ', ' + shipment.destCountry : ''}` : '—'],
                      ]
                ).map(([k, v]) => (
                  <div key={k}>
                    <p className="uppercase font-semibold tracking-wide mb-1" style={{ fontSize: '9px', color: '#4a6080' }}>{k}</p>
                    <p className="text-xs font-bold" style={{ color: '#1a1a2e' }}>{v}</p>
                  </div>
                ))}
              </div>
              {/* Row 2 — 5 items */}
              <div className="grid grid-cols-5 gap-8">
                {(
                  isSeaShipment
                    ? [
                        ['Exp Inv No:', (() => { const raw = shipment.invNo || shipment.invoiceNoDt || ''; return raw.replace(/\s*\d{1,2}\/\d{1,2}\/\d{2,4}\s*/g, '').trim() || '—'; })()],
                        ['Date:', shipment.invoiceDate || shipment.leoDate || new Date(formData.invoiceDate).toLocaleDateString('en-GB')],
                        ['Container Qty:', shipment.containerQty || '—'],
                        ['Container No.:', shipment.containerNo || '—'],
                        ['Buyer:', shipment.buyerName || '—'],
                      ]
                    : [
                        ['Exp Inv No:', shipment.invoiceNumber || shipment.exportInvNo || '—'],
                        ['Date:', shipment.invoiceDate || shipment.exportDate || new Date(formData.invoiceDate).toLocaleDateString('en-GB')],
                        ['Ch. Weight:', weightText(shipment.chWeight || shipment.chargeableWeight)],
                        ['Airline:', (() => { const a = getAirlineFromAWB(shipment.awbNumber); return a ? `${a.code} - ${a.name}` : (shipment.airline || shipment.airlineName || '—'); })()],
                        ['Flight No.:', shipment.flightNo || '—'],
                      ]
                ).map(([k, v]) => (
                  <div key={k}>
                    <p className="uppercase font-semibold tracking-wide mb-1" style={{ fontSize: '9px', color: '#4a6080' }}>{k}</p>
                    <p className="text-xs font-bold" style={{ color: '#1a1a2e' }}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>

          {/* ── LINE ITEMS TABLE ── */}
          {/* Takes the slack on the page, so the totals ride down with the
              footer instead of hanging under a short table. */}
          <div className="px-8 py-5" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
            <div className="border border-gray-300 overflow-hidden">
              <table className="invoice-table w-full text-xs" style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: totals.isIntraState ? '24%' : '26%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  {totals.isIntraState ? (
                    <><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /></>
                  ) : (
                    <col style={{ width: '10%' }} />
                  )}
                  <col style={{ width: '13%' }} />
                </colgroup>
                <thead className="text-white" style={{ background: 'linear-gradient(135deg, #0f2167 0%, #1a3fad 70%, #2563eb 100%)' }}>
                  <tr>
                    <th className="px-2 py-3 text-center font-bold uppercase text-xs">#</th>
                    <th className="px-2 py-3 text-left font-bold uppercase text-xs">Description</th>
                    <th className="px-2 py-3 text-center font-bold uppercase text-xs">SAC</th>
                    <th className="px-2 py-3 text-center font-bold uppercase text-xs">QTY</th>
                    <th className="px-2 py-3 text-right font-bold uppercase text-xs">Rate (₹)</th>
                    <th className="px-2 py-3 text-right font-bold uppercase text-xs">Amount (₹)</th>
                    {totals.isIntraState ? (
                      <>
                        <th className="px-2 py-3 text-right font-bold uppercase text-xs">CGST (₹)</th>
                        <th className="px-2 py-3 text-right font-bold uppercase text-xs">SGST (₹)</th>
                      </>
                    ) : (
                      <th className="px-2 py-3 text-right font-bold uppercase text-xs">IGST (₹)</th>
                    )}
                    <th className="px-2 py-3 text-right font-bold uppercase text-xs">Total (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {formData.lineItems.map((item, index) => {
                    const svc = lookupService(item.description);
                    const descLabel = svc.label || item.description;
                    const hsnCode = item.hsn || svc.hsn || '';
                    const gstRate = item.gst || svc.gst || '18';


                    const itemTotal = calculateLineItemTotal(item);
                    const itemGST = calculateLineItemGST(item);
                    const itemCGST = totals.isIntraState ? itemGST / 2 : 0;
                    const itemSGST = totals.isIntraState ? itemGST / 2 : 0;
                    const itemIGST = !totals.isIntraState ? itemGST : 0;
                    const itemGrandTotal = itemTotal + (totals.isIntraState ? itemCGST + itemSGST : itemIGST);
                    const fmt = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                    return (
                      <tr key={index} className="hover:bg-gray-50 transition border-b border-gray-100">
                        <td className="px-2 py-2.5 text-xs text-gray-600 text-center">{index + 1}</td>
                        <td className="px-2 py-2.5 text-xs text-gray-900 font-medium" style={{ wordBreak: 'break-word' }}>
                          {descLabel}
                        </td>
                        <td className="px-2 py-2.5 text-xs text-gray-600 text-center" style={{ whiteSpace: 'nowrap' }}>{hsnCode}</td>
                        <td className="px-2 py-2.5 text-xs text-gray-900 text-center font-semibold">{item.qty}</td>
                        <td className="px-2 py-2.5 text-xs text-gray-900 text-right" style={{ whiteSpace: 'nowrap' }}>
                          {parseFloat(String(item.rate).replace(/,/g, '')).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 py-2.5 text-xs text-gray-900 text-right font-bold" style={{ whiteSpace: 'nowrap' }}>{fmt(itemTotal)}</td>
                        {totals.isIntraState ? (
                          <>
                            <td className="px-2 py-2.5 text-xs text-gray-900 text-right" style={{ whiteSpace: 'nowrap' }}>{fmt(itemCGST)}</td>
                            <td className="px-2 py-2.5 text-xs text-gray-900 text-right" style={{ whiteSpace: 'nowrap' }}>{fmt(itemSGST)}</td>
                          </>
                        ) : (
                          <td className="px-2 py-2.5 text-xs text-gray-900 text-right" style={{ whiteSpace: 'nowrap' }}>{fmt(itemIGST)}</td>
                        )}
                        <td className="px-2 py-2.5 text-xs text-gray-900 text-right font-bold" style={{ whiteSpace: 'nowrap' }}>
                          {fmt(itemGrandTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ flex: '1 1 auto', minHeight: '20px' }} aria-hidden="true" />

            {/* ── TOTALS AREA ── */}
            <div className="no-break invoice-section">
              <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'flex-end' }}>
                {/* Amount in Words — 58% */}
                <div style={{ flex: '0 0 58%', background: '#f0f5ff', border: '1px solid #c7d7f9', padding: '14px 20px', borderLeft: '4px solid #1a3fad', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', margin: 0, marginBottom: '6px' }}>Amount in Words</p>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#111827', margin: 0, lineHeight: '1.5' }}>
                    {amountInWords(totals.grandTotal)}
                  </p>
                </div>
                {/* Grand Total breakdown — 42% */}
                <div style={{ flex: '0 0 calc(42% - 20px)', background: '#f0f5ff', border: '1px solid #c7d7f9', padding: '12px 20px', borderLeft: '4px solid #1a3fad', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#374151' }}>
                    <span>Gross Total (Taxable)</span>
                    <span style={{ fontWeight: 600 }}>₹{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {totals.isIntraState ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#374151' }}>
                        <span>CGST</span>
                        <span style={{ fontWeight: 600 }}>₹{totals.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#374151' }}>
                        <span>SGST</span>
                        <span style={{ fontWeight: 600 }}>₹{totals.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#374151' }}>
                      <span>IGST</span>
                      <span style={{ fontWeight: 600 }}>₹{totals.igst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div style={{ borderTop: '1.5px solid #1a3fad', marginTop: '6px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280' }}>Grand Total</span>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#1a3fad', whiteSpace: 'nowrap' }}>₹ {totals.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── BANK DETAILS / QR / SIGNATURE ── */}
          <div className="no-break invoice-footer px-8 py-4" style={{ borderTop: '1px solid #e5e7eb' }}>
            <div className="flex flex-row justify-between gap-8 items-start">
              {/* Bank Details */}
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="w-1 h-6 bg-indigo-600 rounded"></div>
                  Bank Details for Payment
                </h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                  {[
                    ['Beneficiary Name', bankDetails.beneficiaryName],
                    ['Bank Name', bankDetails.bankName],
                    ['Account Number', bankDetails.accountNumber],
                    ['IFSC Code', bankDetails.ifscCode],
                    ['Branch', bankDetails.bankBranch],
                    ['SWIFT Code', bankDetails.swiftCode],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-gray-400 font-semibold text-xs uppercase" style={{fontSize:'9px'}}>{k}</p>
                      <p className="text-gray-900 font-bold text-xs">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* QR Code */}
              <div className="flex flex-col items-center justify-center px-4 border-l border-r border-gray-200">
                <p className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">Scan here to Pay</p>
                <img
                  src={bankName === 'HDFC' ? '/images/hdfc_qr.jpeg' : '/images/icici_qr.png'}
                  alt="Scan to Pay"
                  className="w-28 h-28 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <p className="text-gray-500 text-xs mt-1 text-center">UPI Payment</p>
              </div>
              {/* Authorized Signature */}
              <div className="flex flex-col items-center justify-center pl-8">
                <p className="font-bold text-gray-900 text-sm mb-2">For HMR Spedition Pvt. Ltd.</p>
                <img
                  src="/images/sign.png"
                  alt="Authorized Signature"
                  className="h-24 w-auto object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <p className="font-bold text-gray-800 text-sm mt-2">Authorized Signature</p>
              </div>
            </div>
          </div>

          {/* ── TERMS & CERTIFICATION LOGOS ── */}
          <div className="no-break invoice-footer px-8 py-4 border-t border-gray-200">
            <div className="flex items-start justify-between gap-8">
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900 mb-3">Terms &amp; Conditions</h3>
                <div className="text-gray-600 space-y-1 leading-relaxed" style={{ fontSize: '10px' }}>
                  <p>• Payments to be made by crossed Cheque / Demand Draft only in favour of HMR Spedition Pvt Ltd.</p>
                  <p>• We reserve the right to charge Interest @ 24% per annum on bills not paid within agreed credit period.</p>
                  <p>• For any discrepancy, please revert within 5 days from receipt of bill, else invoice will be considered correct.</p>
                  <p>• All disputes are subject to Delhi Jurisdiction.</p>
                </div>
              </div>
              <div className="flex flex-col justify-end" style={{ minWidth: '200px', alignSelf: 'stretch' }}>
                <div className="flex items-center gap-3 mt-auto">
                  {[
                    { src: '/images/logos/iso-logo.png', alt: 'ISO Certified' },
                    { src: '/images/logos/msme-logo.png', alt: 'MSME' },
                    { src: '/images/logos/ffi-logo.webp', alt: 'FFI' },
                    { src: '/images/logos/dcba-logo.jpg', alt: 'DCBA' },
                    { src: '/images/logos/IATA.png', alt: 'IATA' },
                  ].map(logo => (
                    <img key={logo.alt} src={logo.src} alt={logo.alt} className="h-9 w-auto object-contain opacity-80" onError={(e) => { e.target.style.display = 'none'; }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── FOOTER BAR with Gradient ── */}
          <div className="invoice-footer px-8 py-4" style={{ background: 'linear-gradient(135deg, #0f2167 0%, #1a3fad 50%, #2563eb 100%)', borderTop: '2px solid #1a3fad' }}>
            <div className="text-center space-y-1">
              <p className="font-semibold text-base" style={{ color: '#ffffff' }}>Thank you for your business!</p>
              <p className="text-sm" style={{ color: '#bfdbfe' }}>
                ✉️ accounts@hmrspedition.com &nbsp;|&nbsp; 🌐 www.hmrspedition.com
              </p>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .no-print { display: flex; }
        @media print {
          .no-print { display: none !important; }
          @page { margin: 0; size: A4 portrait; }
          body { margin: 0; padding: 0; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .invoice-section { page-break-inside: avoid; break-inside: avoid; }
        .invoice-table { page-break-inside: auto; }
        .invoice-table thead { display: table-header-group; }
        .invoice-table tr { page-break-inside: avoid; break-inside: avoid; }
        .invoice-footer { page-break-inside: avoid; break-inside: avoid; }
        .no-break { page-break-inside: avoid !important; break-inside: avoid !important; display: block; }
      `}</style>
    </div>
  );
};

export default InvoicePreview;

